"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AnalyticsClient = void 0;
const session_1 = require("./session");
const ANON_KEY = 'tp_analytics_anon_id';
const USER_KEY = 'tp_analytics_user_id';
const QUEUE_KEY = 'tp_analytics_queue';
const DISABLED_KEY = 'tp_analytics_disabled';
class AnalyticsClient {
    constructor(config) {
        this.anonymousId = null;
        this.userId = null;
        this.session = new session_1.SessionManager();
        this.queue = [];
        this.preInitQueue = [];
        this.flushing = false;
        this.timer = null;
        this.disabled = false;
        this.initialized = false;
        this.apiUrl = config.apiUrl.replace(/\/+$/, '');
        this.platform = config.platform;
        this.appVersion = config.appVersion;
        this.storage = config.storage;
        this.fetchImpl = config.fetchImpl ?? fetch.bind(globalThis);
        this.flushIntervalMs = config.flushIntervalMs ?? 30000;
        this.flushBatchSize = config.flushBatchSize ?? 10;
        this.maxStoredEvents = config.maxStoredEvents ?? 1000;
    }
    async init() {
        if (this.initialized)
            return;
        const disabledFlag = await this.storage.getItem(DISABLED_KEY);
        this.disabled = disabledFlag === '1';
        let anon = await this.storage.getItem(ANON_KEY);
        if (!anon) {
            anon = (0, session_1.genUuid)();
            await this.storage.setItem(ANON_KEY, anon);
        }
        this.anonymousId = anon;
        this.userId = await this.storage.getItem(USER_KEY);
        const storedQueue = await this.storage.getItem(QUEUE_KEY);
        if (storedQueue) {
            try {
                const parsed = JSON.parse(storedQueue);
                if (Array.isArray(parsed))
                    this.queue = parsed;
            }
            catch {
                this.queue = [];
            }
        }
        this.initialized = true;
        // Only drain pre-init events if the opt-out flag wasn't set in storage.
        // Otherwise calls that fired during the init window (when `this.disabled`
        // hadn't been hydrated yet) would survive the user's earlier opt-out.
        if (!this.disabled) {
            for (const pending of this.preInitQueue) {
                this.enqueue(this.buildEvent(pending.name, pending.properties, pending.occurredAt, pending.sessionId));
            }
        }
        this.preInitQueue = [];
        this.timer = setInterval(() => { void this.flush(); }, this.flushIntervalMs);
    }
    async identify(userId) {
        this.userId = userId;
        await this.storage.setItem(USER_KEY, userId);
    }
    async reset() {
        this.userId = null;
        this.session.reset();
        await this.storage.removeItem(USER_KEY);
    }
    async disable() {
        this.disabled = true;
        await this.storage.setItem(DISABLED_KEY, '1');
        this.queue = [];
        await this.persistQueue();
    }
    async enable() {
        this.disabled = false;
        await this.storage.setItem(DISABLED_KEY, '0');
    }
    isDisabled() {
        return this.disabled;
    }
    /**
     * Bumps session activity. If the previous session was idle past the TTL,
     * a new session id is rotated in and a session_started event fires.
     */
    notifyActive() {
        if (this.disabled)
            return;
        const rotated = this.session.touch();
        if (rotated) {
            this.track('session_started');
        }
    }
    track(eventName, properties) {
        if (this.disabled)
            return;
        const occurredAt = new Date().toISOString();
        const sessionId = this.session.getSessionId();
        if (!this.initialized || !this.anonymousId) {
            this.preInitQueue.push({ name: eventName, properties, occurredAt, sessionId });
            return;
        }
        this.enqueue(this.buildEvent(eventName, properties, occurredAt, sessionId));
    }
    async flush() {
        if (this.flushing || this.disabled || !this.initialized || this.queue.length === 0)
            return;
        this.flushing = true;
        const batch = this.queue.splice(0, 100);
        await this.persistQueue();
        try {
            const res = await this.fetchImpl(`${this.apiUrl}/analytics/events`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ events: batch }),
            });
            if (!res.ok && (res.status >= 500 || res.status === 429)) {
                this.queue.unshift(...batch);
                await this.persistQueue();
            }
        }
        catch {
            this.queue.unshift(...batch);
            await this.persistQueue();
        }
        finally {
            this.flushing = false;
        }
    }
    stop() {
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }
    }
    buildEvent(eventName, properties, occurredAt, sessionId) {
        if (!this.anonymousId)
            throw new Error('analytics: anonymousId missing');
        const event = {
            clientEventId: (0, session_1.genUuid)(),
            eventName,
            occurredAt,
            anonymousId: this.anonymousId,
            sessionId,
            platform: this.platform,
        };
        if (this.userId)
            event.userId = this.userId;
        if (this.appVersion)
            event.appVersion = this.appVersion;
        if (properties)
            event.properties = properties;
        return event;
    }
    enqueue(event) {
        this.queue.push(event);
        if (this.queue.length > this.maxStoredEvents) {
            this.queue.splice(0, this.queue.length - this.maxStoredEvents);
        }
        void this.persistQueue();
        if (this.queue.length >= this.flushBatchSize) {
            void this.flush();
        }
    }
    async persistQueue() {
        try {
            await this.storage.setItem(QUEUE_KEY, JSON.stringify(this.queue));
        }
        catch {
            // storage unavailable — silently drop persistence; in-memory queue still works
        }
    }
}
exports.AnalyticsClient = AnalyticsClient;
